import { useCallback } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import {
  Stripe,
  StripeCardNumberElementChangeEvent,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
} from "@stripe/stripe-js";

import { usePaymentContext } from "../../../../../../contexts/payment-context";
import {
  usePaymentsClientContext,
  setError,
} from "../../../../../../contexts/payments-client-context";

import {
  AgregatedProcessPaymentResponse,
  FailureTypeValue,
  GeneralStatusValue,
  StripeElement,
  SuccessTypeValue,
} from "../../../../types";
import { useRetrieveSetupIntent } from "./use-retrive-setup-intent";
import {
  HandleStripeFormErrors,
  StripeDefaultSetup,
  StripeSetupType,
  SubmitCardSetup,
  SubmitCardSetupWithRedisErrorHandler,
} from "../consts";
import { useConfirmCardSetup, useSavePaymentMethod } from ".";
import { useFormContext } from "react-hook-form";
import { StripeBillingDetails, StripeFormDetails } from "../../stripe-form";
import {
  StripeElementsNames,
  StripeElementsValidationMap,
} from "../components";
import { useAppContext } from "@ftdr/blueprint-components-react";
import { RegistrationResponse } from "../../../../../../types";
import { payment3_paymentmethodpb } from "@ftdr/payment3_paymentmethod_coordinator-js-client";

type UseStripeElementsReturnValues = {
  submitCardSetup: SubmitCardSetup;
  handleFormErrors: HandleStripeFormErrors;
  stripe: Stripe | null;
  clearRedBorderFromStripeInput: any;
  submitCardSetupWithRedisErrorHandler: SubmitCardSetupWithRedisErrorHandler;
};

type StripeElementType =
  | StripeCardNumberElementChangeEvent["elementType"]
  | StripeCardCvcElementChangeEvent["elementType"]
  | StripeCardExpiryElementChangeEvent["elementType"];

const fieldsToUpdate: (keyof StripeBillingDetails)[] = [
  "name",
  "email",
  "phone",
  "postal_code",
  "city",
  "state",
  "line1",
  "line2",
  "country",
];

export const useStripeElements = (): UseStripeElementsReturnValues => {
  const { formsSetup, onFailure } = usePaymentContext();
  const {
    dispatch,
    createPaymentMethodRegistration,
  } = usePaymentsClientContext();
  const stripe = useStripe();
  const elements = useElements();
  const { confirmCardSetup } = useConfirmCardSetup();
  const { retrieveSetupIntent } = useRetrieveSetupIntent();
  const { handleSavePaymentMethod } = useSavePaymentMethod();
  const { setError: setFormError } = useFormContext<StripeFormDetails>();
  const {
    appSettings: { localizedText },
  } = useAppContext();

  const submitCardSetup: SubmitCardSetup = useCallback(
    async (
      requestData,
      stripeFormData,
      clientSecret,
      registrationID
    ): Promise<AgregatedProcessPaymentResponse> => {
      dispatch(setError({}));

      const confirmCardSetupResponse = await confirmCardSetup(
        {
          ...stripeFormData,
          ...fieldsToUpdate.reduce<StripeBillingDetails>((acc, key) => {
            acc[key] =
              requestData?.stripe?.billingDetails?.[key] ?? stripeFormData[key];
            return acc;
          }, {}),
          ...(requestData?.postal_code
            ? { postal_code: requestData.postal_code }
            : {}),
        },
        clientSecret
      );

      if (confirmCardSetupResponse?.result?.isValid) {
        // STEP 3 Call Setup Intent stripe method to use and save card details
        const setupIntentResponse = await retrieveSetupIntent(clientSecret);
        // STEP 4 If Setup Intent was successfull we call method to save the result in fd backend services
        if (
          setupIntentResponse?.type === FailureTypeValue?.SETUP_CARD_SUCCESS
        ) {
          // STEP 6 Return Agregated Response data
          return handleSavePaymentMethod(
            requestData,
            stripeFormData.saveInWallet,
            setupIntentResponse,
            registrationID
          );
        } else {
          // Return Error If Setup Intent call was unsuccessfull
          return {
            stripe: {
              type: setupIntentResponse?.type,
              stripeFailureResponse: setupIntentResponse?.error,
            },
            generalStatus: GeneralStatusValue.PAYMENT_FAILED,
          };
        }
      } else {
        return {
          ...confirmCardSetupResponse.error,
          generalStatus: GeneralStatusValue.PAYMENT_FAILED,
        };
      }
    },
    [handleSavePaymentMethod, retrieveSetupIntent, dispatch, confirmCardSetup]
  );

  const prevalidateEmptyStripeElements = useCallback(
    (setup: StripeSetupType = StripeDefaultSetup): void => {
      (Object.keys(setup) as Array<keyof typeof setup>)
        .filter((element) => StripeElementsNames.includes(element))
        .forEach((element) => {
          let stripeElement: StripeElement = null;
          switch (element) {
            case "cardNumber":
              stripeElement = elements?.getElement("cardNumber");
              break;
            case "expirationDate":
              stripeElement = elements?.getElement("cardExpiry");
              break;
            case "cvc":
              stripeElement = elements?.getElement("cardCvc");
              break;
            default:
          }

          if (stripeElement?._empty && !stripeElement?._invalid) {
            if (
              stripeElement?._parent &&
              Array.from(stripeElement?._parent.classList).some((className) =>
                className.includes("--empty")
              )
            ) {
              setFormError(StripeElementsValidationMap[element], {
                type: "required",
                message: localizedText("STRIPE_REQUIRED_FILED_ERROR"),
              });
              stripeElement?._parent.classList.add("border-red");
            }
          }
        });
    },
    [elements, localizedText, setFormError]
  );

  const handleFormErrors = useCallback<HandleStripeFormErrors>(
    (err, resolveStripePayment) => {
      prevalidateEmptyStripeElements(formsSetup?.stripeForm?.fieldsSetup);

      const resultStripeFormError = {
        type: FailureTypeValue.NOT_VALID_STRIPE_FORM,
        stripeFormErrors: err,
      };
      onFailure(resultStripeFormError);
      resolveStripePayment({
        stripe: resultStripeFormError,
        generalStatus: GeneralStatusValue.PAYMENT_FAILED,
      });
    },
    [
      onFailure,
      prevalidateEmptyStripeElements,
      formsSetup?.stripeForm?.fieldsSetup,
    ]
  );

  const clearRedBorderFromStripeInput = useCallback(
    (element: StripeElementType) => {
      let stripeElement: StripeElement = null;
      switch (element) {
        case "cardNumber":
          stripeElement = elements?.getElement("cardNumber");
          break;
        case "cardExpiry":
          stripeElement = elements?.getElement("cardExpiry");
          break;
        case "cardCvc":
          stripeElement = elements?.getElement("cardCvc");
          break;
        default:
      }

      stripeElement?._parent?.classList?.remove("border-red");
    },
    [elements]
  );

  const submitCardSetupWithRedisErrorHandler: SubmitCardSetupWithRedisErrorHandler = useCallback(
    async (requestData, formData, clientSecret, registrationID) => {
      let submitCardResult = await submitCardSetup(
        requestData,
        formData,
        clientSecret,
        registrationID
      );
      let registrationResult: RegistrationResponse | undefined = undefined;

      switch (submitCardResult?.stripe?.type) {
        //this error handling is because of possibly inactivity of user for 30 minutes,
        // in this case we will retry to add a card automatically with the new registration package
        case FailureTypeValue.REDIS_CACHE_404_ERROR:
          registrationResult = await createPaymentMethodRegistration({
            paymentMethod: {
              details: {
                type: payment3_paymentmethodpb.PaymentMethodType.CreditCard,
              },
            },
          });
          submitCardResult = await submitCardSetupWithRedisErrorHandler(
            requestData,
            formData,
            registrationResult?.registration?.stripe?.setupIntentClientSecret ||
              "",
            registrationResult?.registration?.registrationID || ""
          );
          break;
        case SuccessTypeValue.STRIPE_SUCCESS:
          registrationResult = await createPaymentMethodRegistration({
            paymentMethod: {
              details: {
                type: payment3_paymentmethodpb.PaymentMethodType.CreditCard,
              },
            },
          });
          break;
      }

      return submitCardResult;
    },
    [createPaymentMethodRegistration, submitCardSetup]
  );

  return {
    handleFormErrors,
    submitCardSetup,
    stripe,
    clearRedBorderFromStripeInput,
    submitCardSetupWithRedisErrorHandler,
  };
};
